import { FormattedMessage } from 'react-intl';
import { journeyElementsStore } from 'Global/JourneyElements/JourneyElementsStore';
import { appConfigStore } from 'Models/AppConfigStore';
import { roundToDecimals } from 'Util/_HumanWritten/NumberUtils';
import { convertWeight, roundWeightForUnit, weightUnitLabel } from 'Util/Weight';
import { OverbookingWarningDto } from './OverbookingWarningDto';

const formattedMessages = {
	newCapacityLabel: 'reservation__overbooking-modal__new-capacity',
	capacityAfterBookingLabel: 'reservation__overbooking-modal__capacity-after-boooking-label',
} as const;

interface OverbookingInsightDetailsProps {
	overbookingWarning: OverbookingWarningDto;
}

export function OverbookingInsightDetails({ overbookingWarning }: OverbookingInsightDetailsProps) {
	const { displayName, usedBefore, usedAfter, maxPossible, stopId, extraInfo, capacityType } = overbookingWarning;

	// Weight capacities arrive in canonical kg — convert them to the configured
	// display unit (and label them) like the rest of the app. Non-weight
	// capacities are rounded to 2 decimal places so every number in the modal
	// (overbooking amount and capacity-after-booking) displays consistently.
	const isWeight = capacityType === 'Weight';
	const weightUnit = appConfigStore.weightDisplayUnit;
	const displayValue = (value: number) =>
		isWeight ? roundWeightForUnit(convertWeight(value, 'kg', weightUnit), weightUnit) : roundToDecimals(value, 2);
	const unitSuffix = isWeight ? ` ${weightUnitLabel(weightUnit)}` : '';

	return (
		<>
			<div className="overbooking-modal__journey-insight__section">
				<div className="overbooking-modal__journey-insight__row">
					<div className="overbooking-modal__journey-insight__label">Capacity type</div>
					<div>{displayName}</div>
				</div>
				<div className="overbooking-modal__journey-insight__row">
					<div className="overbooking-modal__journey-insight__label">
						<FormattedMessage id={formattedMessages.newCapacityLabel} defaultMessage="Overbooking amount" />
					</div>
					<div style={{ color: 'red' }}>{`${displayValue(usedAfter - maxPossible)}${unitSuffix}`}</div>
				</div>
				<div className="overbooking-modal__journey-insight__row">
					<div className="overbooking-modal__journey-insight__label">
						<FormattedMessage
							id={formattedMessages.capacityAfterBookingLabel}
							defaultMessage="Capacity (after booking)"
						/>
					</div>
					<div style={{ color: 'red' }}>
						{displayValue(usedAfter)}/{displayValue(maxPossible)}
						{unitSuffix}
					</div>
				</div>
			</div>
			<StopInfo id={stopId} />
			{extraInfo && <div style={{ marginTop: '0.5rem' }}>{extraInfo}</div>}
		</>
	);
}

function StopInfo({ id }: { id: string }) {
	const getStopId = journeyElementsStore(x => x.getStopId);
	const stop = getStopId(id);

	if (stop && stop.route.stops.length > 2) {
		return (
			<>
				This overbooking occurs at {stop.location.name} (Stop {stop.order}), which has the lowest remaining
				capacity on this route.
			</>
		);
	}

	return <></>;
}
